import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles, X } from 'lucide-react'
import type { LandingPersona, LandingPersonaId } from '@/hooks/useLandingPersona'

interface Props {
  persona: LandingPersona
  personaId: LandingPersonaId
  setPersonaId: (id: LandingPersonaId) => void
  PERSONAS: Record<LandingPersonaId, LandingPersona>
}

const HINT_KEY = 'jarvis.vibehint.seen'

export default function LandingVibeSwitcher({
  persona,
  personaId,
  setPersonaId,
  PERSONAS,
}: Props) {
  const [open, setOpen] = useState(false)
  const [showHint, setShowHint] = useState(false)

  // First-visit discovery nudge (remembered so it shows once).
  useEffect(() => {
    try {
      if (!localStorage.getItem(HINT_KEY)) setShowHint(true)
    } catch {
      /* localStorage blocked — skip the hint */
    }
  }, [])

  const dismissHint = () => {
    setShowHint(false)
    try {
      localStorage.setItem(HINT_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  // Lock background scroll while the sheet is open.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const pick = (id: LandingPersonaId) => {
    setPersonaId(id)
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8)
  }

  const chipStyle: CSSProperties = {}
  const dotStyle: CSSProperties = {
    background: persona.primary,
    boxShadow: `0 0 12px ${persona.glow}`,
  }

  return (
    <>
      <button
        className={`lp-vibe-chip ${showHint ? 'hint' : ''}`}
        style={chipStyle}
        onClick={() => {
          setOpen(true)
          dismissHint()
        }}
        aria-label={`Change vibe — current: ${persona.name}`}
        aria-haspopup="dialog"
      >
        <span className="lp-vibe-dot" style={dotStyle} />
        <span className="lp-vibe-name">{persona.name}</span>
        <Sparkles size={14} />
      </button>

      {showHint && (
        <span className="lp-vibe-hint" role="status">
          Tap to change the vibe →
        </span>
      )}

      {open && createPortal(
        <div
          className="lp-sheet-scrim"
          data-sharp={persona.sharp ? 'true' : 'false'}
          onClick={() => setOpen(false)}
        >
          <div
            className="lp-sheet"
            role="dialog"
            aria-label="Choose your vibe"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lp-sheet-grab" aria-hidden />
            <div className="lp-sheet-head">
              <h3>Choose your vibe</h3>
              <button
                className="lp-sheet-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="lp-sheet-sub">
              Same Jarvis. Five personalities. Tap one to feel it.
            </p>

            <div className="lp-sheet-list">
              {(Object.values(PERSONAS) as LandingPersona[]).map((p) => {
                const active = personaId === p.id
                const orbStyle: CSSProperties = {
                  background: `radial-gradient(circle at 35% 30%, ${p.secondary}, ${p.primary})`,
                  boxShadow: active ? `0 0 22px ${p.glow}` : 'none',
                }
                return (
                  <button
                    key={p.id}
                    className={`lp-vibe-row ${active ? 'active' : ''}`}
                    onClick={() => pick(p.id)}
                    style={{ borderColor: active ? p.primary : undefined }}
                  >
                    <span className="lp-vibe-orb" style={orbStyle} />
                    <span className="lp-vibe-text">
                      <span className="lp-vibe-row-name">{p.name}</span>
                      <span className="lp-vibe-row-tag">{p.accentText}</span>
                    </span>
                    {active && (
                      <span
                        className="lp-vibe-check"
                        style={{ color: p.primary }}
                        aria-hidden
                      >
                        ●
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
